import { DateTime } from 'luxon';
import R from 'ramda';

import { SpecialHoursEntry, SpecialHoursInputEntry } from './types';
import { DEFAULT_TIME_PAIR } from './values';
import { DAYS_OF_WEEK } from '../values';

const MINUTE_DURATION_MS = 60 * 1000;
const HOUR_DURATION_MS = 60 * 60 * 1000;

const checkForIncompatibleData = (exception: SpecialHoursEntry) => {
  const openingTimes = R.uniq(DAYS_OF_WEEK.map(weekday => exception.opening_times[weekday])).filter(
    time => time.length,
  );

  if (openingTimes.length > 1) {
    return true;
  }

  return false;
};

export const fromExceptionToEntry = (exception: SpecialHoursEntry, timezone: string): SpecialHoursInputEntry => {
  if (checkForIncompatibleData(exception)) {
    throw new Error('Exception contains incompatible data');
  }

  const closedAllDay = R.equals(exception.opening_times, {
    friday: [],
    monday: [],
    saturday: [],
    sunday: [],
    tuesday: [],
    thursday: [],
    wednesday: [],
  });

  const startDateTime = DateTime.fromMillis(exception.period.start.$date).setZone(timezone);
  const endDateTime = DateTime.fromMillis(exception.period.end.$date).setZone(timezone);

  const period = {
    start: startDateTime.startOf('day'),
    end: endDateTime.minus({ days: 1 }).startOf('day'),
  };

  const openingTimes = R.uniq(DAYS_OF_WEEK.map(weekday => exception.opening_times[weekday]).flat());

  const open24Hours =
    openingTimes.length === 2 &&
    !!R.find(val => R.equals(val, { type: 'open', value: { $date: 0 } }))(openingTimes) &&
    !!R.find(val => R.equals(val, { type: 'close', value: { $date: 24 * HOUR_DURATION_MS } }))(openingTimes);

  const openEvents = openingTimes.filter(event => event.type === 'open');
  const closeEvents = openingTimes.filter(event => event.type === 'close');

  const timePairs = openEvents.map(openEvent => {
    return {
      start: openEvent.value.$date,
      end: closeEvents
        .map(closeEvent =>
          closeEvent.value.$date > openEvent.value.$date
            ? closeEvent.value.$date
            : closeEvent.value.$date + 24 * HOUR_DURATION_MS,
        )
        .sort((a, b) => a - b)[0],
    };
  });

  return {
    message: exception.message,
    closedAllDay,
    open24Hours,
    periods: [period],
    timePairs:
      open24Hours || closedAllDay
        ? [DEFAULT_TIME_PAIR]
        : timePairs.map(pair => ({
            startMinutes: pair.start / MINUTE_DURATION_MS,
            endMinutes: pair.end / MINUTE_DURATION_MS,
          })),
    modifiedBy: exception.modified_by,
  };
};

const fromSpecialHoursToSpecialHoursInputEntries = (
  times: SpecialHoursEntry[],
  timezone: string,
): SpecialHoursInputEntry[] => {
  const sortedExceptions = R.sort((a, b) => a.period.start.$date - b.period.start.$date, times);
  const filteredExceptions = sortedExceptions.filter(
    exception => DateTime.fromMillis(exception.period.end.$date).setZone(timezone) > DateTime.local().setZone(timezone),
  );
  return filteredExceptions.map(time => {
    return fromExceptionToEntry(time, timezone);
  });
};

export default fromSpecialHoursToSpecialHoursInputEntries;
